// InvoiceFieldsDialog.jsx — الحقول المخصصة لنوع فاتورة (تظهر في رأس الفاتورة)
// ضعه بجانب InvoiceTypesPage.jsx
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, IconButton, Tooltip, CircularProgress, MenuItem,
  Dialog, DialogContent, DialogActions, Switch, FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DynamicFormOutlinedIcon from '@mui/icons-material/DynamicFormOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import axios from '../../axios';
import { fieldSx, BRAND } from '../../components/Reports/ReportKit';

export const FIELD_TYPES = [
  { value: 'string', label: 'نص' },
  { value: 'decimal', label: 'رقم' },
  { value: 'date', label: 'تاريخ' },
  { value: 'list', label: 'قائمة اختيار' },
  { value: 'bool', label: 'نعم / لا' },
];
const typeLabel = (v) => FIELD_TYPES.find(t => t.value === v)?.label ?? v;

const errMsg = (e, fb) => {
  const d = e?.response?.data;
  return (typeof d === 'string' && d) || d?.message || d?.title || fb;
};

// ألوان صريحة للأزرار: ثيم التطبيق يغيّر الأزرار النصية فيصبح النص بلون الخلفية
const primaryBtnSx = {
  fontWeight: 800, borderRadius: '10px', px: 2, boxShadow: 'none', bgcolor: '#4f46e5', color: '#fff',
  '&:hover': { bgcolor: '#4338ca', boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#c7d2fe', color: '#fff' },
  '& .MuiButton-startIcon': { ml: .5, mr: -.25 },
};
const secondaryBtnSx = {
  fontWeight: 700, borderRadius: '10px', px: 2, bgcolor: '#fff', color: '#475569', border: '1px solid #e2e8f0',
  '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
  '&.Mui-disabled': { bgcolor: '#fff', color: '#cbd5e1', borderColor: '#f1f5f9' },
  '& .MuiButton-startIcon': { ml: .5, mr: -.25 },
};

const EMPTY = { id: 0, displayName: '', dataType: 'string', isRequired: false, showInPrint: true, options: '' };

export default function InvoiceFieldsDialog({ companyId, type, onClose, onChanged }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);     // null = مغلق | { ...EMPTY } إضافة/تعديل
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/InvoiceFields', { params: { companyId, invoiceTypeId: type.invoiceTypeId } });
      setFields(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(errMsg(e, 'تعذر تحميل الحقول'));
    } finally {
      setLoading(false);
    }
  }, [companyId, type.invoiceTypeId]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e?.preventDefault();
    if (!form.displayName.trim()) { toast.error('أدخل اسم الحقل'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        companyId,
        invoiceTypeId: type.invoiceTypeId,
        sortOrder: form.id ? form.sortOrder : 0,
      };
      if (form.id) await axios.put(`/InvoiceFields/${form.id}`, payload);
      else await axios.post('/InvoiceFields', payload);
      toast.success(form.id ? 'تم تعديل الحقل' : 'تمت إضافة الحقل');
      setForm(null);
      await load();
      onChanged?.();
    } catch (e2) {
      toast.error(errMsg(e2, 'تعذر حفظ الحقل'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f) => {
    const ask = await Swal.fire({
      icon: 'warning', title: 'حذف الحقل',
      text: f.usedCount
        ? `الحقل "${f.displayName}" مستخدم في ${f.usedCount} فاتورة — سيُخفى من الفواتير الجديدة وتبقى قيمه في الفواتير السابقة.`
        : `حذف الحقل "${f.displayName}"؟`,
      showCancelButton: true, confirmButtonText: 'حذف', cancelButtonText: 'إلغاء',
      confirmButtonColor: '#e11d48', reverseButtons: true, focusCancel: true,
    });
    if (!ask.isConfirmed) return;
    try {
      await axios.delete(`/InvoiceFields/${f.id}`, { params: { companyId } });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, 'تعذر حذف الحقل'));
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[idx], next[j]] = [next[j], next[idx]];
    const ordered = next.map((f, i) => ({ ...f, sortOrder: i + 1 }));
    setFields(ordered);
    try {
      await axios.put('/InvoiceFields/reorder', ordered.map(f => ({ id: f.id, sortOrder: f.sortOrder })), { params: { companyId } });
    } catch (e) {
      toast.error(errMsg(e, 'تعذر حفظ الترتيب'));
      load();
    }
  };

  const used = form?.id ? fields.find(f => f.id === form.id)?.usedCount || 0 : 0;

  return (
    <Dialog open onClose={() => !saving && onClose()} dir="rtl" maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 1.75, bgcolor: BRAND.primary, color: '#fff' }}>
        <DynamicFormOutlinedIcon />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 16 }}>الحقول المخصصة</Typography>
          <Typography sx={{ fontSize: 12.5, opacity: .85 }}>{type.name} — تظهر في رأس الفاتورة</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} size="small" sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : (
          <Box>
            {fields.length === 0 && !form && (
              <Box sx={{ py: 5, px: 3, textAlign: 'center', color: '#94a3b8' }}>
                <DynamicFormOutlinedIcon sx={{ fontSize: 44, mb: 1, color: '#cbd5e1' }} />
                <Typography sx={{ fontWeight: 700, color: '#64748b' }}>لا توجد حقول لهذا النوع</Typography>
                <Typography sx={{ fontSize: 13 }}>مثال: رقم السيارة، اسم المستلم، رقم أمر الشراء</Typography>
              </Box>
            )}

            {fields.map((f, i) => (
              <Box key={f.id} sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.1, borderBottom: '1px solid #f1f5f9',
                bgcolor: form?.id === f.id ? BRAND.primarySoft : 'transparent',
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} sx={{ p: .25 }}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></IconButton>
                  <IconButton size="small" disabled={i === fields.length - 1} onClick={() => move(i, 1)} sx={{ p: .25 }}><ArrowDownwardIcon sx={{ fontSize: 16 }} /></IconButton>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14.5 }}>
                    {f.displayName}{f.isRequired && <Box component="span" sx={{ color: '#e11d48', mr: .5 }}>*</Box>}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: .75, alignItems: 'center', flexWrap: 'wrap', mt: .25 }}>
                    <Box sx={{ px: 1, py: .1, borderRadius: '999px', fontSize: 11.5, fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569' }}>{typeLabel(f.dataType)}</Box>
                    {f.dataType === 'list' && <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{(f.options || []).join('، ')}</Typography>}
                    {f.showInPrint && <Tooltip title="يظهر في الطباعة"><PrintOutlinedIcon sx={{ fontSize: 15, color: '#94a3b8' }} /></Tooltip>}
                    {f.usedCount > 0 && <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>· في {f.usedCount} فاتورة</Typography>}
                  </Box>
                </Box>
                <Tooltip title="تعديل">
                  <IconButton size="small" onClick={() => setForm({ ...EMPTY, ...f, options: (f.options || []).join('\n') })} sx={{ color: '#d97706' }}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="حذف">
                  <IconButton size="small" onClick={() => remove(f)} sx={{ color: '#e11d48' }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}

            {form && (
              <Box component="form" onSubmit={save} sx={{ p: 2, display: 'grid', gap: 1.5, bgcolor: '#fafbfc', borderTop: '1px solid #e5e7eb' }}>
                <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{form.id ? 'تعديل الحقل' : 'حقل جديد'}</Typography>
                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr' } }}>
                  <TextField size="small" autoFocus label="اسم الحقل" value={form.displayName}
                    onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} sx={fieldSx} />
                  <TextField size="small" select label="النوع" value={form.dataType} disabled={used > 0}
                    helperText={used > 0 ? 'لا يتغير بعد الاستخدام' : ' '}
                    onChange={e => setForm(f => ({ ...f, dataType: e.target.value }))} sx={fieldSx}>
                    {FIELD_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </TextField>
                </Box>
                {form.dataType === 'list' && (
                  <TextField size="small" multiline minRows={3} label="الاختيارات (سطر لكل اختيار)" value={form.options}
                    onChange={e => setForm(f => ({ ...f, options: e.target.value }))} sx={fieldSx} />
                )}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel control={<Switch checked={!!form.isRequired} onChange={e => setForm(f => ({ ...f, isRequired: e.target.checked }))} />} label="إلزامي" />
                  <FormControlLabel control={<Switch checked={!!form.showInPrint} onChange={e => setForm(f => ({ ...f, showInPrint: e.target.checked }))} />} label="يظهر في الطباعة" />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={() => setForm(null)} disabled={saving} sx={secondaryBtnSx}>إلغاء</Button>
                  <Button type="submit" variant="contained" disableElevation disabled={saving}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                    sx={primaryBtnSx}>
                    حفظ الحقل
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: '1px solid #eef0f4', justifyContent: 'space-between' }}>
        <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => setForm({ ...EMPTY })} disabled={!!form || loading}
          sx={primaryBtnSx}>
          إضافة حقل
        </Button>
        <Button variant="outlined" onClick={onClose} disabled={saving} sx={secondaryBtnSx}>إغلاق</Button>
      </DialogActions>
    </Dialog>
  );
}